import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { initializeApp } from 'firebase/app';
import { getConfigVar } from '../config';

import { Loadable, Notification as NotificationType } from '../types';
import { AuthContext } from './AuthContext';
import { DataContext } from './DataContext';
import { DAY } from '../constants/TimeConstants';
import { notificationService } from '../api/services/notificationService';

interface NotificationContextType {
  interval: number;
  setInterval: (interval: number) => void;
  notifications: Loadable<NotificationType>;
  reloadNotifications: () => Promise<void>;
  resolveNotification: (notificationId: string, action: string, data?: any) => Promise<void>;
  resolveNotifications: (domain: string, ids: Array<string>, action: string) => Promise<void>;
  showNotifications: boolean;
  setShowNotifications: (show: boolean) => void;
}

const initialContext: NotificationContextType = {
  interval: DAY,
  setInterval: () => {},
  notifications: { loading: true },
  reloadNotifications: () => Promise.resolve(),
  resolveNotification: () => Promise.resolve(),
  resolveNotifications: () => Promise.resolve(),
  showNotifications: false,
  setShowNotifications: () => {},
};

const NotificationContext = createContext<NotificationContextType>(initialContext);
const NOTIFICATION_REFRESH_TIMEOUT = parseInt(
  getConfigVar('REACT_APP_NOTIFICATION_REFRESH_TIMEOUT', '300000')!,
);
const VAPID_KEY = getConfigVar('REACT_APP_FIREBASE_VAPID_KEY');
const firebaseConfig = {
  apiKey: getConfigVar('REACT_APP_FIREBASE_API_KEY'),
  authDomain: getConfigVar('REACT_APP_FIREBASE_AUTH_DOMAIN'),
  projectId: getConfigVar('REACT_APP_FIREBASE_PROJECT_ID'),
  storageBucket: getConfigVar('REACT_APP_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getConfigVar('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'),
  appId: getConfigVar('REACT_APP_FIREBASE_APP_ID'),
};

function NotificationProvider({ children }: { children: ReactNode }) {
  const { auth } = useContext(AuthContext);
  const { reloadDevices } = useContext(DataContext);
  const [timer, setTimer] = useState(0);
  const [notifications, setNotifications] = useState<Loadable<NotificationType>>({ loading: true });
  const [showNotifications, setShowNotifications] = useState(false);
  const [interval, setInterval] = useState(DAY);

  // Firebase messaging is initialized once on mount; reloadNotifications is
  // captured by closure inside onMessage handlers and reads the latest version.
  //
  // Flow: feature-detect (isSupported) → resolve permission → register the
  // SW explicitly → getToken with that SW handle → set up onMessage. The
  // explicit SW registration lets the foreground onMessage hand off to
  // `registration.showNotification()`, which routes the click through the
  // SW's `notificationclick` handler (server template) so deep-linking
  // behaves the same in foreground and background. Bypassing the SW with
  // `new Notification(...)` would drop the click handler.
  useEffect(() => {
    if (!('Notification' in window)) {
      console.log('Notifications not supported in this browser');
      return;
    }

    let cancelled = false;

    const setupMessaging = async () => {
      try {
        // Firebase's `isSupported()` returns false on browsers that lack
        // service workers / IndexedDB / push API (older Safari, private
        // browsing). Skipping the rest avoids throwing during getMessaging().
        const supported = await isSupported();
        if (!supported) {
          console.log('Firebase messaging is not supported in this browser');
          return;
        }

        // Resolve permission. `default` → ask the user; otherwise honor.
        let permission = Notification.permission;
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') {
          console.log('Notification permission denied by user');
          return;
        }
        if (cancelled) return;

        // Register the SW ourselves so we can hand the same registration
        // to both getToken (for FCM) and onMessage (for foreground
        // showNotification). Without this, getToken auto-registers but
        // doesn't return a handle we can reuse.
        const swRegistration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
        );

        const messaging = getMessaging(initializeApp(firebaseConfig));

        try {
          const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: swRegistration,
          });
          if (!cancelled) registerToken(token);
        } catch (err) {
          console.log('Error registering push messaging token', err);
        }

        if (cancelled) return;

        onMessage(messaging, (payload) => {
          if (payload.notification) {
            const title = payload.notification.title ?? '';
            const options: NotificationOptions = {
              body: payload.notification.body,
              icon: '/favicon.ico',
              badge: '/favicon.ico',
              data: payload.data,
              requireInteraction: true,
              tag: payload.data?.type || 'general',
            };
            // Use the SW registration so the notification's click event
            // is dispatched to firebase-messaging-sw.js's `notificationclick`
            // listener, matching the background-message path.
            swRegistration.showNotification(title, options).catch((err) => {
              console.log('Error showing foreground notification', err);
            });
          }
          reloadNotifications().then(() => {});
        });
      } catch (err) {
        console.log('Error initializing messaging', err);
      }
    };

    setupMessaging();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic refresh: each run schedules the next tick by bumping `timer`.
  useEffect(() => {
    setTimeout(() => setTimer(timer + 1), NOTIFICATION_REFRESH_TIMEOUT);
    if (auth && auth.token) {
      // Wait a short time to ensure a token is properly set in apiClient
      setTimeout(() => {
        reloadNotifications().catch((err) => console.log('Error reloading notifications', err));
      }, 500);
    }
  }, [auth, timer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh when interval changes; same rationale as the periodic effect above.
  useEffect(() => {
    if (auth && auth.token) {
      // Wait a short time to ensure a token is properly set in apiClient
      setTimeout(() => {
        reloadNotifications().catch((err) => console.log('Error reloading notifications', err));
      }, 500);
    }
  }, [interval]); // eslint-disable-line react-hooks/exhaustive-deps

  const registerToken = (token: string) => notificationService.registerToken(token);

  const reloadNotifications = async () => {
    if (auth) {
      setNotifications(() => ({ loading: true }));
      try {
        const response = await notificationService.getNotifications(interval.toString());
        return setNotifications({ loading: false, data: response.data });
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403)
          window.location.href = getConfigVar('REACT_APP_BASE_HREF', window.location.origin)!;
        else {
          setNotifications({ loading: false, error: err as Error });
        }
      }
    }
    return Promise.resolve();
  };

  const resolveNotification = async (notificationId: string, action: string, data?: any) => {
    const response = await notificationService.performNotificationAction(
      notificationId,
      action,
      data,
    );
    setNotifications({
      ...notifications,
      data: notifications.data?.filter((n) => n.id !== notificationId),
    });
    if (response.data.reloadDevices)
      reloadDevices().catch((err) => console.log('Error reloading devices', err));
  };

  const resolveNotifications = async (domain: string, ids: Array<string>, action: string) => {
    const response = await notificationService.performBulkNotificationAction(action, ids, domain);
    setNotifications({
      ...notifications,
      data: notifications.data?.filter((n) => !ids.includes(n.id)),
    });
    if (response.data.reloadDevices)
      reloadDevices().catch((err) => console.log('Error reloading devices', err));
  };

  return (
    <NotificationContext.Provider
      value={{
        interval,
        setInterval,
        notifications,
        reloadNotifications,
        resolveNotification,
        resolveNotifications,
        showNotifications,
        setShowNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export { NotificationContext, NotificationProvider };
